import {ee} from "../../services/earth-engine";
import { aoiGeometry } from '../../common/aoi';
import {maskLandsatCloudsRatio} from "../satellite/landsat";
import {maskS2ImageMask} from "../satellite/sentinel";







/**
 * The transect measurement and the classifier live in one place now; see
 * {@link module:LAND_COVER_TRANSECTS}. Re-exported rather than imported by the consumers so every
 * existing import site keeps reading them from this module.
 */
export { extractShoreLineTurf, landCoversIntersectionsTurf, trainAndClassify } from '../landCoverTransects';

/**
 * @function filterElevation
 * @description Masks out pixels above the given elevation threshold, using the ALOS DSM 30m
 * digital elevation model.
 * 1. For MapBiomas classifications: reprojects the DSM to the classification image's projection,
 *    subtracts it from the classification, and masks out where the DSM has no data.
 * 2. For Sentinel-2 (S2): uses the SRTM 30m DEM directly as the elevation mask.
 * 3. Otherwise (Landsat): uses the ALOS DSM boolean mask (elevation <= threshold) directly.
 * @param {ee.Image} image - Image to mask.
 * @param {Number} elevation - Elevation threshold, in meters.
 * @param {String} [type=null] - Pass "mapbiomas" to use the MapBiomas-specific masking path.
 * @param {String} [satellite="LANDSAT"] - Satellite name; "S2" uses the SRTM DEM path.
 * **Called by:
 * @see {getMapBiomasClassificationYear}
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} The image with elevation-masked pixels removed.
 */
export const filterElevation = (image, elevation, type = null, satellite = "LANDSAT") => {
  let elevationMask;

  let dataset = ee.ImageCollection('JAXA/ALOS/AW3D30/V3_2').select(0).mosaic().lte(elevation);

  if (type === "mapbiomas") {
    const dataset_reproj = dataset.reproject({
      crs: image.projection(),
      scale: image.projection().nominalScale(),
    });

    const classification_minus_dataset = image.subtract(dataset_reproj);

    const mask = dataset_reproj.eq(0).not();

    elevationMask = classification_minus_dataset.updateMask(mask);
    return image.updateMask(elevationMask)
  } else {
    if (satellite === "S2") {
      dataset = ee.Image('USGS/SRTMGL1_003');
      elevationMask = dataset.lte(elevation);
    } else {
      elevationMask = dataset;
    }
  }

  return image.updateMask(elevationMask);

}

/**
 * @function applyScaleFactors
 * @description Rescales a Landsat Collection 2 image's optical and thermal bands from raw digital
 * numbers to reflectance/temperature units.
 * @param {ee.Image} image - Landsat image with SR_B* and ST_B* bands.
 * **Called by:
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} The image with rescaled optical/thermal bands.
 */
export const applyScaleFactors = (image) => {
  const opticalBands = image.select('SR_B.').multiply(0.0000275).add(-0.2);
  const thermalBand = image.select('ST_B.*').multiply(0.00341802).add(149.0);
  return image.addBands(opticalBands, null, true)
    .addBands(thermalBand, null, true);
}

/**
 * @function getMapBiomasClassificationsList
 * @description Lists every classification band (year) available in the MapBiomas Collection 9
 * land-use/land-cover asset.
 * **Called by:
 * @see {LandCover}
 * @memberof module:CSQUEEZE
 * @returns {Array<String>} Band ids, one per classified year.
 */
export const getMapBiomasClassificationsList = () => {
  const mapbiomas = ee.Image("projects/mapbiomas-public/assets/brazil/lulc/collection9/mapbiomas_collection90_integration_v1").getInfo();
  return mapbiomas.bands.map((m) => m.id);
}

/**
 * @function getMapBiomasClassificationYear
 * @description Clips a single year's MapBiomas classification band to the AOI and applies the
 * MapBiomas-specific elevation mask.
 * @param {Array} AOI - Area of interest polygon coordinates.
 * @param {Number} year - Classification year to select.
 * @param {Number} [elevation=10] - Elevation threshold, in meters.
 * @see {@link filterElevation}
 * **Called by:
 * @see {LandCover}
 * @memberof module:CSQUEEZE
 * @returns {ee.Image} The elevation-masked classification image for the given year.
 */
export const getMapBiomasClassificationYear = (AOI, year, elevation = 10) => {
  const requested = parseInt(year, 10);
  if (!Number.isFinite(requested)) {
    throw new Error(`CSqueeze: "${year}" is not a MapBiomas classification year.`);
  }
  let classified = ee.Image("projects/mapbiomas-public/assets/brazil/lulc/collection9/mapbiomas_collection90_integration_v1")
    .select("classification_" + requested).clip(aoiGeometry(AOI));
  return filterElevation(classified, elevation, "mapbiomas");
}

/**
 * @function getLandCoverLabelMapBiomas
 * @description Maps a raw MapBiomas classification code to CSqueeze's 5-class land-cover label
 * (0 mangrove, 1 vegetation, 2 water, 3 human intervention, 4 beach/dune/sand spot), defaulting
 * to human intervention (3) for unrecognized codes.
 * @param {Number} value - Raw MapBiomas classification code.
 * **Called by:
 * @see {handleTransformClassToPolygons}
 * @memberof module:CSQUEEZE
 * @returns {Number} CSqueeze land-cover label (0-4).
 */
export function getLandCoverLabelMapBiomas(value) {
  let landcoverstypes = {
    mangrove: {
      label: 0,
      types: [5]
    },
    vegetation: {
      label: 1,
      types: [1, 3, 4, 5, 49, 10, 11, 12, 32, 29, 50, 13]
    },
    water: {
      label: 2,
      types: [26, 33]
    },
    human: {
      label: 3,
      types: [14, 15, 18, 19, 39, 20, 40, 62, 41, 36, 46, 47, 48, 9, 21, 22, 24, 30, 25, 31, 27, 75]
    },
    beach: {
      label: 4,
      types: [23]
    },
  }

  for (const type of Object.entries(landcoverstypes)) {
    if (type[1].types.includes(value)) {
      return type[1].label;
    }
  }

  return 3;
}

/**
 * @function maskCloudLandsat
 * @description Masks cloudy pixels in a Landsat image based on the QA band's cloud ratio.
 * @param {ee.Image} image - Landsat image to mask.
 * @param {String} qa_band - Name of the QA band.
 * @param {ee.Geometry} geometry - Area of interest geometry.
 * @see {@link maskLandsatCloudsRatio}
 * **Called by:
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} The cloud-masked image.
 */
export function maskCloudLandsat(image, qa_band, geometry) {
  return maskLandsatCloudsRatio (image,geometry,qa_band);
}

/**
 * @function maskCloudSentinel
 * @description Masks cloudy pixels in a Sentinel-2 image and rescales it to reflectance (0-1).
 * @param {ee.Image} image - Sentinel-2 image to mask.
 * @param {Object} mission - Mission configuration object.
 * @see {@link maskS2ImageMask}
 * **Called by:
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} The cloud-masked, rescaled image.
 */
export function maskCloudSentinel(image, mission) {
  return maskS2ImageMask(image, mission).divide(10000);
}

/**
 * @function applyNdviBand
 * @description Computes the Normalized Difference Vegetation Index (NDVI) band.
 * @param {ee.Image} image - Source image.
 * @param {Object} bands - Named band mapping, with `nir` and `red`.
 * **Called by:
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} Single-band image named "NDVI".
 */
export const applyNdviBand = (image, bands) =>
  image.expression('(NIR - RED) / (NIR + RED)', {
    'NIR': image.select(bands.nir),
    'RED': image.select(bands.red),
  }).rename('NDVI')

/**
 * @function applyMndwiBand
 * @description Computes the Modified Normalized Difference Water Index (MNDWI) band.
 * @param {ee.Image} image - Source image.
 * @param {Object} bands - Named band mapping, with `green` and `swir`.
 * **Called by:
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} Single-band image named "MNDWI".
 */
export const applyMndwiBand = (image, bands) =>
  image.expression('(GREEN - SWIR) / (GREEN + SWIR)', {
    'GREEN': image.select(bands.green),
    'SWIR': image.select(bands.swir),
  }).rename('MNDWI')

/**
 * @function applyUiBand
 * @description Computes the Urban Index (UI) band.
 * @param {ee.Image} image - Source image.
 * @param {Object} bands - Named band mapping, with `swir` and `nir`.
 * **Called by:
 * @see {handleAcquireImagesMedian} - CSqueeze module's saga.
 * @see {handleAcquireImage} - MEPBay module's saga, multi-year acquisition path.
 * @memberof module:SHARED
 * @returns {ee.Image} Single-band image named "UI".
 */
export const applyUiBand = (image, bands) =>
  image.expression('(SWIR - NIR) / (SWIR + NIR)', {
    'SWIR': image.select(bands.swir),
    'NIR': image.select(bands.nir),
  }).rename('UI')


/**
 * @function getMangroves
 * @description Builds a Google Maps tile overlay of ESA WorldCover mangrove pixels (class 95),
 * visualized via their terrain slope.
 * **Called by:
 * @see {AOIChooser}
 * @memberof module:CSQUEEZE
 * @returns {google.maps.ImageMapType} Tile overlay of mangrove areas.
 */
export const getMangroves = function () {
  const dataset = ee.ImageCollection("ESA/WorldCover/v200").first();

  const mangroveMask = dataset.select('Map').eq(95);

  const mangroves = dataset.updateMask(mangroveMask);

  const visualization = {
    min: 95,
    max: 95,
    palette: ['000000', 'red']
  };

  const slope = ee.Terrain.slope(mangroves);
  const mapId = slope.getMap(visualization);
  const tileSource = new ee.layers.EarthEngineTileSource(mapId);
  return new ee.layers.ImageOverlay(tileSource)
}